import * as THREE from 'three';
import '../ui/house.css';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { HUD } from '../ui/hud.js';
import { getState, save } from '../core/save.js';
import { advanceTime, sleepUntilMorning } from '../core/world.js';
import { spendForAction, restoreEnergy } from '../core/actions.js';
import { RECIPES, canCook, cook } from '../core/recipes.js';
import { flagObjective } from '../core/quests.js';
import { getIngredient, ingredientName } from '../core/ingredients.js';
import { getDish, getDishes, eatDish } from '../core/dishes.js';
import { activeBuffSummary } from '../core/buffs.js';

// Bread Boi's House — the cozy pet-care interior and the heart of the game.
// A warm little room with four care stations (fridge, toy corner, shower, bed).
// Walk Bread Boi up to one, tap the prompt, and top up the matching need. Needs
// gently drift down over real time, so there's always a little something to do.
// Ported from the proven kitchen prototype's digital-pet loop.

export function createHouseScene(){
  return () => new HouseScene();
}

// warm interior palette
const FLOOR_A = 0xF3E2BE;
const FLOOR_B = 0xE9D2A6;
const WALL    = 0xF6E6C9;
const WALL_D  = 0xE7D2AC;

// the four needs, in panel order. `spot` is where Bread Boi stands, `look` the
// furniture he turns to face, `label` the enter-prompt copy.
const CARE = [
  { key: 'hunger', icon: '🍞', name: 'hunger', label: '🍞 grab a snack',   say: 'yum! Bread Boi digs into the fridge. hunger topped up!',
    spot: new THREE.Vector3(-3.4, 0, -2.3), look: new THREE.Vector3(-5.2, 0, -4.4) },
  { key: 'fun',    icon: '🎮', name: 'fun',    label: '🎮 play a while',    say: 'wheee! toys + cartoons. Bread Boi is having a blast!',
    spot: new THREE.Vector3( 3.4, 0, -2.3), look: new THREE.Vector3( 5.2, 0, -4.4) },
  { key: 'clean',  icon: '🚿', name: 'clean',  label: '🚿 hop in the tub',  say: 'splish splash! all fresh and crumb-free now.',
    spot: new THREE.Vector3(-3.4, 0,  1.6), look: new THREE.Vector3(-5.4, 0,  2.6) },
  { key: 'energy', icon: '😴', name: 'energy', label: '😴 take a nap',      say: 'zzz… a cozy little rest. Bread Boi feels recharged!',
    spot: new THREE.Vector3( 3.4, 0,  1.6), look: new THREE.Vector3( 5.4, 0,  2.6) }
];

const CARE_GAIN = 25;           // per interaction, clamped to 100

// gentle per-second decay (roughly a couple of points a minute each)
const DECAY = { hunger: 0.05, fun: 0.045, clean: 0.035, energy: 0.03 };

class HouseScene {
  constructor(){
    this.dom = [];              // DOM panels to tear down on exit
    this.hopT = 0; this.hopDur = 0.6;
    this._saveT = 0; this._uiT = 0;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xF2E3C6);

    this.camera = new THREE.PerspectiveCamera(52, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 10.5;
    this.camTarget = new THREE.Vector3(0, 1.6, 0);
    this.azimuth = 0; this.polar = 0.46;

    this._buildLights();
    this._buildRoom();
    this._buildKitchen();
    this._buildToyCorner();
    this._buildBath();
    this._buildBed();
    this._buildDecor();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 2);
    this.bread.root.rotation.y = Math.PI;   // facing into the room
    this.scene.add(this.bread.root);

    this.hotspots = [
      ...CARE.map(c => ({
        pos: c.spot, r: 1.9, label: c.label,
        action: () => this._care(c)
      })),
      { pos: new THREE.Vector3(-2.0, 0, -4.8), r: 1.9, label: '🍳 cook a meal', action: () => this._openKitchen() },
      { pos: new THREE.Vector3(0, 0, 5.4), r: 2.0, label: '← back to Toast Town',
        action: () => this.manager.goTo('town', { from: 'house' }) }
    ];
    this.nearSpot = null;
  }

  // ---- room shell ----
  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0xFFF3DF, 0.82));
    const key = new THREE.DirectionalLight(0xFFEFCE, 0.6);
    key.position.set(5, 11, 6); key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    const s = 12;
    key.shadow.camera.left = -s; key.shadow.camera.right = s;
    key.shadow.camera.top = s; key.shadow.camera.bottom = -s;
    this.scene.add(key);
    const fill = new THREE.DirectionalLight(0xF7D9A8, 0.22);
    fill.position.set(-6, 5, 5); this.scene.add(fill);
  }

  _buildRoom(){
    // checkerboard floor (canvas texture like the prototype)
    const cv = document.createElement('canvas'); cv.width = cv.height = 128;
    const g = cv.getContext('2d');
    g.fillStyle = '#F3E2BE'; g.fillRect(0, 0, 128, 128);
    g.fillStyle = '#E9D2A6'; g.fillRect(0, 0, 64, 64); g.fillRect(64, 64, 64, 64);
    const tex = new THREE.CanvasTexture(cv);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping; tex.repeat.set(7, 7);
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(14, 14), new THREE.MeshLambertMaterial({ map: tex }));
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true; this.scene.add(floor);

    const wallMat = new THREE.MeshLambertMaterial({ color: WALL });
    const back = new THREE.Mesh(new THREE.PlaneGeometry(14, 7), wallMat);
    back.position.set(0, 3.5, -7); back.receiveShadow = true; this.scene.add(back);
    const sideL = new THREE.Mesh(new THREE.PlaneGeometry(14, 7), wallMat);
    sideL.rotation.y = Math.PI/2; sideL.position.set(-7, 3.5, 0); sideL.receiveShadow = true; this.scene.add(sideL);
    const sideR = sideL.clone(); sideR.rotation.y = -Math.PI/2; sideR.position.set(7, 3.5, 0); this.scene.add(sideR);
    // baseboards
    box(14, 0.3, 0.12, WALL_D, 0, 0.15, -6.94, this.scene);
    box(0.12, 0.3, 14, WALL_D, -6.94, 0.15, 0, this.scene);
    box(0.12, 0.3, 14, WALL_D, 6.94, 0.15, 0, this.scene);

    // a sunny window on the back wall
    this._buildWindow(-0.2, 4.0, -6.92);

    // signage + doorway home (front, +z, behind the camera-facing wall gap)
    const marquee = textSprite("BREAD BOI'S HOUSE", 26, '#8C6239', 4.6, 1.0);
    marquee.position.set(0, 5.9, -6.8); this.scene.add(marquee);
    this.exitDoor = box(1.6, 2.6, 0.3, C.crustDark, 0, 1.3, 5.7, this.scene);
    box(1.9, 0.35, 0.4, C.rye, 0, 2.75, 5.7, this.scene);           // door lintel
    sphere(0.09, C.toast, 0.55, 1.2, 5.55, this.scene);             // knob
    const sign = textSprite('← to Toast Town', 22, '#3A2A1C', 2.8, 0.8);
    sign.position.set(0, 3.3, 5.55); this.scene.add(sign);

    // a soft round rug in the middle of the room
    const rug = new THREE.Group();
    const rugA = new THREE.Mesh(new THREE.CircleGeometry(2.0, 32), new THREE.MeshLambertMaterial({ color: C.raspberry }));
    rugA.rotation.x = -Math.PI/2; rugA.receiveShadow = true; rug.add(rugA);
    const rugB = new THREE.Mesh(new THREE.RingGeometry(1.45, 1.7, 32), new THREE.MeshLambertMaterial({ color: C.cream }));
    rugB.rotation.x = -Math.PI/2; rugB.position.y = 0.006; rug.add(rugB);
    rug.position.set(0, 0.012, 0.3); this.scene.add(rug);
  }

  _buildWindow(x, y, z){
    const g = new THREE.Group();
    const sky = new THREE.Mesh(new THREE.PlaneGeometry(2.8, 2.1), new THREE.MeshLambertMaterial({ color: C.sky }));
    sky.position.z = 0.01; g.add(sky);
    const sun = new THREE.Mesh(new THREE.CircleGeometry(0.44, 24), new THREE.MeshLambertMaterial({ color: C.tangerine }));
    sun.position.set(0.8, 0.5, 0.02); g.add(sun);
    const cloud = new THREE.Mesh(new THREE.CircleGeometry(0.32, 20), new THREE.MeshLambertMaterial({ color: C.white }));
    cloud.position.set(-0.6, 0.15, 0.02); g.add(cloud);
    const cloud2 = cloud.clone(); cloud2.scale.setScalar(0.7); cloud2.position.set(-0.25, 0.0, 0.02); g.add(cloud2);
    // frame
    const fm = C.cocoa;
    box(3.0, 0.16, 0.1, fm, 0, 1.08, 0.03, g); box(3.0, 0.16, 0.1, fm, 0, -1.08, 0.03, g);
    box(0.16, 2.3, 0.1, fm, -1.42, 0, 0.03, g); box(0.16, 2.3, 0.1, fm, 1.42, 0, 0.03, g);
    box(0.1, 2.1, 0.06, fm, 0, 0, 0.04, g);
    g.position.set(x, y, z); this.scene.add(g);
  }

  // ---- station 1: kitchen + fridge (hunger), back-left ----
  _buildKitchen(){
    // counter run along the back wall
    box(4.6, 1.5, 1.4, C.sprout, -3.6, 0.75, -5.9, this.scene);
    box(4.8, 0.14, 1.6, C.cream, -3.6, 1.57, -5.85, this.scene);
    // a sink basin + faucet on the counter
    box(1.0, 0.06, 0.8, C.sink, -4.8, 1.66, -5.85, this.scene);
    const faucet = new THREE.Group();
    cyl(0.06, 0.06, 0.55, C.cocoa, 0, 0.27, 0, faucet, 8);
    const spout = cyl(0.05, 0.05, 0.42, C.cocoa, 0, 0.5, 0.18, faucet, 8); spout.rotation.x = Math.PI/2;
    faucet.position.set(-4.8, 1.64, -6.2); this.scene.add(faucet);
    // a jam jar on the counter for colour
    cyl(0.16, 0.13, 0.3, C.raspberry, -2.7, 1.78, -5.7, this.scene, 14);
    cyl(0.17, 0.17, 0.05, C.cocoa, -2.7, 1.95, -5.7, this.scene, 14);

    // stove / oven for the cooking minigame
    this.stove = new THREE.Group(); this.stove.position.set(-2.0, 0, -5.95); this.scene.add(this.stove);
    box(1.3, 1.25, 1.2, 0x444444, 0, 0.625, 0, this.stove);          // oven body
    box(1.32, 0.08, 1.22, 0x222222, 0, 1.25, 0, this.stove);          // cooktop
    cyl(0.12, 0.12, 0.04, 0x111111, -0.35, 1.29, -0.25, this.stove, 12); // burner
    cyl(0.12, 0.12, 0.04, 0x111111,  0.35, 1.29, -0.25, this.stove, 12);
    cyl(0.12, 0.12, 0.04, 0x111111, -0.35, 1.29,  0.25, this.stove, 12);
    cyl(0.12, 0.12, 0.04, 0x111111,  0.35, 1.29,  0.25, this.stove, 12);
    box(0.9, 0.55, 0.06, 0xAADDFF, 0, 0.65, 0.62, this.stove);         // oven window
    this.stoveLight = new THREE.PointLight(0xFFAA55, 0, 4, 1.5);
    this.stoveLight.position.set(-2.0, 2.4, -5.95); this.scene.add(this.stoveLight);

    // the fridge
    const fridge = new THREE.Group();
    box(1.6, 3.4, 1.6, C.cobalt, 0, 1.7, 0, fridge);
    box(1.2, 3.0, 1.15, C.cream, 0.16, 1.7, 0, fridge);
    box(1.05, 0.08, 1.1, C.sink, 0.18, 1.35, 0, fridge);           // shelf lines
    box(1.05, 0.08, 1.1, C.sink, 0.18, 2.25, 0, fridge);
    box(0.28, 0.5, 0.28, C.white, 0.2, 2.55, -0.2, fridge);        // handle
    box(0.3, 0.28, 0.3, C.raspberry, 0.2, 1.55, 0.3, fridge);      // magnet
    fridge.position.set(-5.6, 0, -4.4); this.scene.add(fridge);
    this.fridge = fridge;
  }

  // ---- station 2: TV + toy corner (fun), back-right ----
  _buildToyCorner(){
    // TV on a low stand
    box(2.2, 0.7, 0.9, C.rye, 5.2, 0.35, -5.6, this.scene);
    box(2.6, 1.5, 0.22, C.crustDark, 5.2, 1.7, -6.1, this.scene);   // TV body
    box(2.2, 1.15, 0.06, C.breeze, 5.2, 1.75, -5.98, this.scene);   // screen
    // a little scene on the screen
    box(1.9, 0.3, 0.02, C.sprout, 5.2, 1.35, -5.94, this.scene);    // hills
    sphere(0.18, C.tangerine, 5.75, 2.0, -5.93, this.scene, 12);    // sun
    // toy chest + toys on the floor
    box(1.3, 0.7, 0.9, C.tangerine, 4.4, 0.35, -3.9, this.scene);
    box(1.35, 0.1, 0.95, C.crust, 4.4, 0.72, -3.9, this.scene);
    const ball = sphere(0.4, C.raspberry, 5.6, 0.4, -3.6, this.scene, 16);
    ball.rotation.z = 0.4;
    box(0.35, 0.35, 0.35, C.cobalt, 5.9, 0.18, -4.4, this.scene);   // blocks
    box(0.3, 0.3, 0.3, C.sprout, 6.2, 0.15, -3.9, this.scene);
    this.toyBall = ball;
  }

  // ---- station 3: shower / tub (clean), front-left ----
  _buildBath(){
    // tiled splash-wall behind the tub
    box(0.14, 3.0, 3.2, 0xCFE7EE, -6.85, 1.5, 2.4, this.scene);
    // the tub
    const tub = new THREE.Group();
    box(2.4, 0.9, 1.5, C.white, 0, 0.45, 0, tub);
    box(2.1, 0.6, 1.2, C.breeze, 0, 0.55, 0, tub);                  // water
    [[-1.15, -0.7], [1.15, -0.7], [-1.15, 0.7], [1.15, 0.7]].forEach(p =>
      cyl(0.09, 0.09, 0.3, C.cocoa, p[0], 0.15, p[1], tub, 8));     // feet
    tub.position.set(-5.4, 0, 2.6); this.scene.add(tub);
    this.tub = tub;
    // shower head + pipe on the wall
    const pipe = new THREE.Group();
    cyl(0.05, 0.05, 1.8, C.sink, 0, 0.9, 0, pipe, 8);
    const arm = cyl(0.05, 0.05, 0.5, C.sink, 0, 1.8, 0.2, pipe, 8); arm.rotation.x = Math.PI/2.4;
    cyl(0.22, 0.16, 0.14, C.sink, 0, 2.1, 0.55, pipe, 12);          // head
    pipe.position.set(-6.7, 1.0, 2.6); this.scene.add(pipe);
    // a rubber duck for good measure
    sphere(0.18, C.tangerine, -5.4, 1.0, 2.6, this.scene, 12);
    sphere(0.1, C.tangerine, -5.25, 1.12, 2.72, this.scene, 10);    // head
  }

  // ---- station 4: bed (energy), front-right ----
  _buildBed(){
    const bed = new THREE.Group();
    box(2.6, 0.5, 3.4, C.rye, 0, 0.25, 0, bed);                    // frame
    box(2.4, 0.35, 3.2, C.cream, 0, 0.6, 0, bed);                  // mattress
    box(2.4, 0.3, 2.0, C.cobalt, 0, 0.85, 0.5, bed);              // blanket
    box(2.0, 0.35, 0.7, C.white, 0, 0.85, -1.2, bed);            // pillow
    box(2.6, 1.2, 0.3, C.crust, 0, 0.85, -1.65, bed);            // headboard
    bed.position.set(5.4, 0, 2.4); this.scene.add(bed);
    this.bed = bed;
    // a little nightstand + lamp
    box(0.7, 0.9, 0.7, C.crust, 3.7, 0.45, 3.2, this.scene);
    cyl(0.06, 0.1, 0.5, C.cocoa, 3.7, 1.15, 3.2, this.scene, 10);
    cyl(0.22, 0.28, 0.3, C.tangerine, 3.7, 1.5, 3.2, this.scene, 14);
  }

  _buildDecor(){
    // potted plant in a back corner
    const g = new THREE.Group();
    cyl(0.4, 0.3, 0.6, 0xC77B4A, 0, 0.3, 0, g, 16);
    cyl(0.42, 0.42, 0.1, 0xA9663B, 0, 0.62, 0, g, 16);
    for (let i = 0; i < 6; i++){
      const leaf = new THREE.Mesh(new THREE.SphereGeometry(0.3, 10, 8), new THREE.MeshLambertMaterial({ color: i % 2 ? C.sprout : C.sproutDim }));
      leaf.scale.set(0.5, 1.3, 0.5);
      const a = i / 6 * Math.PI * 2;
      leaf.position.set(Math.cos(a) * 0.26, 1.1 + Math.sin(i) * 0.2, Math.sin(a) * 0.26);
      leaf.rotation.z = Math.cos(a) * 0.5; leaf.castShadow = true; g.add(leaf);
    }
    g.position.set(6.3, 0, -3.1); this.scene.add(g);

    // framed Bread Boi portrait on the left wall
    const frame = new THREE.Group();
    box(1.4, 1.7, 0.08, C.cocoa, 0, 0, 0, frame);
    box(1.2, 1.5, 0.04, C.cream, 0, 0, 0.04, frame);
    sphere(0.4, C.toast, 0, -0.1, 0.08, frame, 14);
    frame.position.set(-6.85, 3.4, -1.5); frame.rotation.y = Math.PI/2; this.scene.add(frame);
  }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.6];
    this.input.onTap = (ndc, ray) => this._tap(ray);

    this._buildStatPanel();
    this._renderStats();

    HUD.setSubtitle("bread boi's house");
    HUD.say("welcome home! Bread Boi's needs drift down over time — walk up to the fridge, toys, tub or bed and keep the little guy happy.");
    HUD.setHint('click to walk · walk up to a station and tap the prompt to care · or the door home');
    HUD.hideHintSoon(8500);
  }

  exit(){
    this._clearDom();
    this.input.onTap = null;
    HUD.showEnter(null); HUD.say('');
    if (this.pet) save();            // persist the latest needs on the way out
  }

  // ---- stat panel (own DOM, own house.css) ----
  _buildStatPanel(){
    const el = document.createElement('div');
    el.className = 'house-stats';
    el.innerHTML = `<div class="house-stats-title">bread boi</div>` + CARE.map(c =>
      `<div class="house-stat" data-k="${c.key}">
         <span class="hs-lbl">${c.icon} ${c.name}</span>
         <span class="hs-bar"><i></i></span>
       </div>`).join('');
    document.body.appendChild(el);
    this.dom.push(el);
    this.statFills = {};
    CARE.forEach(c => { this.statFills[c.key] = el.querySelector(`.house-stat[data-k="${c.key}"] i`); });
  }

  _clearDom(){ this.dom.forEach(d => d.remove()); this.dom = []; this.statFills = null; }

  _renderStats(){
    const pet = getState().pet;
    if (!this.statFills) return;
    for (const c of CARE){
      const v = Math.round(pet[c.key]);
      const fill = this.statFills[c.key];
      fill.style.width = Math.max(0, Math.min(100, v)) + '%';
      fill.style.background = v < 30 ? 'var(--raspberry)' : v < 60 ? 'var(--tangerine)' : 'var(--sprout)';
    }
  }

  // ---- caring for a need ----
  _care(c){
    const pet = getState().pet;
    this.bread.stop();
    // face the furniture
    const to = new THREE.Vector3().subVectors(c.look, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z);
    this.bread.root.rotation.y = this.bread.facing;

    if (c.key === 'energy'){
      // real sleep: pose + time-skip to morning
      this._sleep();
      return;
    }

    const check = spendForAction('nap');
    if (!check.ok){
      HUD.say(`Too tired to play right now. ${check.reason}`);
      return;
    }

    // top up the need
    const before = pet[c.key];
    pet[c.key] = Math.min(100, pet[c.key] + CARE_GAIN);
    advanceTime(0.5);
    save();
    this._renderStats();
    // reaction: a happy face + a little hop
    this.bread.drawFace(before >= 100 ? 'grin' : 'yum');
    this.hopT = this.hopDur;
    HUD.say(before >= 100 ? `Bread Boi's ${c.name} is already full — but enjoys it anyway!` : c.say);
  }

  _sleep(){
    const pet = getState().pet;
    this.sleepT = 2.5;
    this.bread.startPose('sleep', 2.5);
    this.bread.drawFace('effort');
    this.input.onTap = null;
    HUD.say('zzz… Bread Boi is out cold.');
    sleepUntilMorning();
    pet.energy = 100;
    restoreEnergy(100); // also restore player energy
    pet.hunger = Math.max(0, pet.hunger - 10);   // wake up a little hungry
    save();
    this._renderStats();
    HUD.refreshClock();
    HUD.refreshEnergy();
  }

  _tap(ray){
    if (this.sleepT > 0) return;
    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-5.6, Math.min(5.6, gp.x));
    gp.z = Math.max(-2.2, Math.min(4.9, gp.z));      // stay clear of the back furniture
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  update(dt){
    this.bread.update(dt);

    if (this.sleepT > 0){
      this.sleepT -= dt;
      if (this.sleepT <= 0){
        this.bread.stopPose();
        this.bread.drawFace('grin');
        this.input.onTap = (ndc, ray) => this._tap(ray);
        HUD.say('Bread Boi wakes up bright and early! energy maxed out.');
      }
      // orbit camera still follows while sleeping
      this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
      this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
      this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.4, 1.6, this.bread.root.position.z * 0.4), 0.06);
      this.camera.position.set(
        this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
        this.camTarget.y + this.camDist * Math.sin(this.polar),
        this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
      );
      this.camera.lookAt(this.camTarget);
      return;
    }

    // needs decay gently over real time; persist + repaint on a slow cadence so
    // the debounced save() actually gets a chance to flush.
    const pet = getState().pet;
    this.pet = pet;
    let changed = false;
    for (const k in DECAY){
      const nv = Math.max(0, pet[k] - DECAY[k] * dt);
      if (nv !== pet[k]){ pet[k] = nv; changed = true; }
    }
    this._uiT += dt;
    if (this._uiT >= 0.5){ this._uiT = 0; this._renderStats(); }
    this._saveT += dt;
    if (changed && this._saveT >= 5){ this._saveT = 0; save(); }

    // little hop reaction after caring for a need
    if (this.hopT > 0){
      this.hopT -= dt;
      const k = 1 - Math.max(0, this.hopT) / this.hopDur;   // 0 → 1
      const arc = Math.sin(k * Math.PI);
      this.bread.root.position.y = arc * 0.42;
      this.bread.armL.shoulder.rotation.x = -1.1 * arc;
      this.bread.armR.shoulder.rotation.x = -1.1 * arc;
      if (this.hopT <= 0){ this.bread.resetLimbs(); this.bread.drawFace('grin'); }
    }

    // proximity hotspots → enter prompt (walking suppresses re-triggering)
    let near = null, nd = Infinity;
    this.hotspots.forEach(h => { const d = h.pos.distanceTo(this.bread.root.position); if (d < nd){ nd = d; near = h; } });
    if (near && nd < near.r){
      if (this.nearSpot !== near){
        this.nearSpot = near;
        HUD.showEnter(near.label, () => near.action());
      }
    } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }

    // orbit camera, softly following Bread Boi
    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.4, 1.6, this.bread.root.position.z * 0.4), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  // ---- kitchen / cooking minigame -----------------------------------------
  _openKitchen(){
    this.bread.stop();
    this._clearDom();
    HUD.showEnter(null);
    this._renderKitchen('recipes');
  }

  _renderKitchen(tab = 'recipes'){
    this._clearDom();
    const inv = getState().inventory;
    const buffs = activeBuffSummary();

    let body = '';
    if (tab === 'recipes'){
      const cards = Object.entries(RECIPES).map(([id, r]) => {
        const ings = Object.entries(r.ingredients).map(([key, need]) => {
          const have = key.startsWith('crop:') ? (inv.crops[key.slice(5)] || 0) : (inv.ingredients?.[key] || 0);
          const name = key.startsWith('crop:') ? key.slice(5) : ingredientName(key);
          return `<span class="${have >= need ? 'ok' : 'short'}">${name} ${have}/${need}</span>`;
        }).join(' · ');
        const can = canCook(id);
        return `
          <div class="recipe-card ${can ? 'can' : ''}">
            <div class="r-icon">${r.icon}</div>
            <div class="r-name">${r.name}</div>
            <div class="r-desc">${r.desc}</div>
            <div class="r-ingredients">${ings}</div>
            <button class="cook-btn" data-id="${id}" ${can ? '' : 'disabled'}>cook</button>
          </div>`;
      }).join('');
      body = `<div class="recipe-grid">${cards}</div>`;
    } else {
      const dishes = getDishes();
      const ids = Object.keys(RECIPES).filter(id => dishes[id] > 0);
      const rows = ids.length ? ids.map(id => {
        const r = RECIPES[id];
        const buffText = Object.entries(r.buff).map(([s, n]) => `+${n} ${s.toUpperCase()}`).join(', ');
        return `
          <div class="dish-row">
            <span class="d-icon">${r.icon}</span>
            <span class="d-name">${r.name}</span>
            <span class="d-count">x${dishes[id]}</span>
            <span class="d-buff">${buffText} · ${Math.round(r.duration / 60)}m</span>
            <button class="eat-btn" data-id="${id}">eat</button>
          </div>`;
      }).join('') : '<p class="kitchen-empty">No dishes ready. Cook something first!</p>';
      body = `<div class="dish-list">${rows}</div>`;
    }

    const buffLine = buffs.length
      ? buffs.map(b => `<span class="buff-pill">+${b.amount} ${b.stat.toUpperCase()} · ${b.remaining}s</span>`).join('')
      : '<span class="buff-pill none">no active buffs</span>';

    const el = document.createElement('div');
    el.className = 'kitchen-panel';
    el.innerHTML = `
      <div class="kitchen-card">
        <h2>🍳 Bread Boi's Kitchen</h2>
        <div class="kitchen-tabs">
          <button class="kitchen-tab ${tab === 'recipes' ? 'active' : ''}" data-tab="recipes">Recipes</button>
          <button class="kitchen-tab ${tab === 'dishes' ? 'active' : ''}" data-tab="dishes">Pantry</button>
        </div>
        <div class="kitchen-buffs">${buffLine}</div>
        <div class="kitchen-body">${body}</div>
        <button class="kitchen-close">close</button>
      </div>
    `;
    document.body.appendChild(el);
    this.dom.push(el);

    el.querySelectorAll('.kitchen-tab').forEach(btn => {
      btn.onclick = () => this._renderKitchen(btn.dataset.tab);
    });
    el.querySelectorAll('.cook-btn').forEach(btn => {
      btn.onclick = () => this._startCook(btn.dataset.id);
    });
    el.querySelectorAll('.eat-btn').forEach(btn => {
      btn.onclick = () => this._eatDish(btn.dataset.id);
    });
    el.querySelector('.kitchen-close').onclick = () => this._clearDom();
  }

  _startCook(id){
    if (!canCook(id)) return;
    this._clearDom();
    const recipe = RECIPES[id];
    const el = document.createElement('div');
    el.className = 'cook-game';
    el.innerHTML = `
      <div class="cook-card">
        <h3>cooking ${recipe.icon} ${recipe.name}</h3>
        <div class="cook-track"><div class="cook-target"></div><div class="cook-marker"></div></div>
        <p class="cook-hint">tap / click when the marker is in the green zone</p>
        <button class="cook-stop">stop!</button>
      </div>
    `;
    document.body.appendChild(el);
    this.dom.push(el);

    const marker = el.querySelector('.cook-marker');
    const track = el.querySelector('.cook-track');
    const target = el.querySelector('.cook-target');
    const speed = 3 + recipe.difficulty * 6; // rad/s
    let running = true;
    let t0 = performance.now();
    let raf = null;

    const loop = () => {
      if (!running) return;
      const t = (performance.now() - t0) / 1000;
      const pct = 50 + 42 * Math.sin(t * speed);
      marker.style.left = pct + '%';
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const stop = () => {
      if (!running) return;
      running = false;
      cancelAnimationFrame(raf);
      const rect = track.getBoundingClientRect();
      const markerRect = marker.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const mCenter = markerRect.left + markerRect.width / 2 - rect.left;
      const tCenter = targetRect.left + targetRect.width / 2 - rect.left;
      const maxDist = targetRect.width / 2;
      const dist = Math.abs(mCenter - tCenter);
      const quality = Math.max(0, 1 - dist / Math.max(1, maxDist));
      this._finishCook(id, quality);
    };
    el.querySelector('.cook-stop').onclick = stop;
    el.onclick = (e) => { if (e.target === el) stop(); };
  }

  _finishCook(id, quality){
    const result = cook(id, quality);
    this._clearDom();
    const recipe = RECIPES[id];
    if (!result.ok){
      this._renderKitchen('recipes');
      HUD.say('Cooking failed: ' + result.reason);
      return;
    }
    if (result.qualityLabel === 'burnt'){
      this.bread.drawFace('o');
      HUD.say(`Burnt the ${recipe.name}! Ingredients wasted.`);
    } else {
      this.bread.drawFace('yum');
      this.bread.startPose('lift', 0.5);
      const q = result.qualityLabel === 'perfect' ? 'perfect' : 'good';
      HUD.say(`${q} ${recipe.icon} ${recipe.name} cooked!${result.learned ? ' Recipe added to your book.' : ''}`);
      flagObjective('chef-in-training', 'cookedFirstDish');
    }
    this._renderKitchen(result.qualityLabel === 'burnt' ? 'recipes' : 'dishes');
    HUD.refreshStats();
  }

  _eatDish(id){
    const res = eatDish(id);
    if (!res.ok){ HUD.say(res.reason); return; }
    const r = RECIPES[id];
    this.bread.drawFace('yum');
    this.bread.startPose('lift', 0.4);
    const buffText = Object.entries(r.buff).map(([s, n]) => `+${n} ${s.toUpperCase()}`).join(', ');
    HUD.say(`Ate ${r.icon} ${r.name}! ${buffText} for ${Math.round(res.duration / 60)} min.`);
    this._renderKitchen('dishes');
    HUD.refreshStats();
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
